/**
 * Layout-matched shimmer skeletons for the Settings tabs.
 *
 * Every settings section shows one of these while its data loads instead of a
 * centered spinner, so the panel keeps its shape and there is no jarring
 * spinner→content swap.
 *
 * The shimmer block itself is `Skeleton` from `@ai-tutor/ui`, re-exported here
 * so the sections' existing imports keep working. It used to be defined
 * locally as `bg-muted dark:bg-white/10`; that is the same rendered colour as
 * the shared primitive's `bg-secondary`, because `--secondary` and `--muted`
 * resolve to identical values in both themes - so adopting the shared one was
 * a pixel-for-pixel no-op.
 */

import { Skeleton } from '@ai-tutor/ui';

export { Skeleton };

/** A bordered card matching the settings section card chrome. */
function SkeletonCard({ children }: { children: React.ReactNode }) {
  return <div className='rounded-lg border border-border p-5'>{children}</div>;
}

/** Mirrors the Profile + Change Password cards in PersonalSection. */
export function PersonalSectionSkeleton() {
  return (
    <div className='space-y-8' data-testid='personal-skeleton'>
      <SkeletonCard>
        <Skeleton className='mb-1 h-4 w-24' />
        <Skeleton className='mb-5 h-3 w-40' />
        <div className='space-y-6'>
          <div className='flex items-center gap-5'>
            <Skeleton className='h-16 w-16 rounded-full' />
            <div className='space-y-2'>
              <Skeleton className='h-3.5 w-28' />
              <Skeleton className='h-3 w-44' />
            </div>
          </div>
          <div className='grid grid-cols-1 gap-5 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <Skeleton className='h-3 w-20' />
              <Skeleton className='h-9 w-full' />
            </div>
            <div className='space-y-1.5'>
              <Skeleton className='h-3 w-24' />
              <Skeleton className='h-9 w-full' />
            </div>
          </div>
          <div className='space-y-1.5'>
            <Skeleton className='h-3 w-12' />
            <Skeleton className='h-[68px] w-full' />
          </div>
          <Skeleton className='h-8 w-16' />
        </div>
      </SkeletonCard>

      <SkeletonCard>
        <Skeleton className='mb-1 h-4 w-36' />
        <Skeleton className='mb-4 h-3 w-48' />
        <div className='max-w-sm space-y-4'>
          <Skeleton className='h-9 w-full' />
          <Skeleton className='h-9 w-full' />
          <Skeleton className='h-8 w-32' />
        </div>
      </SkeletonCard>
    </div>
  );
}

/** Mirrors the Lesson Depth + Lesson Flow cards in LearningStyleSection. */
export function LearningStyleSkeleton() {
  return (
    <div className='space-y-8' data-testid='learning-style-skeleton'>
      <SkeletonCard>
        <Skeleton className='mb-1 h-4 w-28' />
        <Skeleton className='mb-5 h-3 w-64' />
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
          {[0, 1, 2].map(i => (
            <Skeleton key={i} className='h-[68px] w-full' />
          ))}
        </div>
        <Skeleton className='mt-5 h-8 w-16' />
      </SkeletonCard>

      <SkeletonCard>
        <Skeleton className='mb-1 h-4 w-24' />
        <Skeleton className='mb-5 h-3 w-72' />
        <div className='space-y-3'>
          <Skeleton className='h-10 w-full' />
          <Skeleton className='h-10 w-full' />
        </div>
      </SkeletonCard>
    </div>
  );
}
